import { useState, useRef, useLayoutEffect } from 'react';
import { CheckCircle, Circle } from '@phosphor-icons/react';
import { useSceneStore } from '../../stores/sceneStore';
import { useToolStore } from '../../stores/toolStore';
import { getSpineManager, type ViewportState } from '../../lib/spine-manager';
import type { Annotation } from '@spine-workspace/shared';

const RULER_SIZE = 20;

type AnnotationFilter = 'all' | 'open' | 'resolved';

function worldToScreen(wx: number, wy: number, vp: ViewportState): { sx: number; sy: number } {
  return {
    sx: RULER_SIZE + vp.x + wx * vp.zoom,
    sy: RULER_SIZE + vp.y + wy * vp.zoom,
  };
}

function screenToWorld(sx: number, sy: number, vp: ViewportState): { wx: number; wy: number } {
  return {
    wx: (sx - RULER_SIZE - vp.x) / vp.zoom,
    wy: (sy - RULER_SIZE - vp.y) / vp.zoom,
  };
}

/**
 * AnnotationPin — position is updated imperatively via a ref on every viewport
 * change, so panning/zooming never triggers a React re-render of this component.
 */
function AnnotationPin({ annotation }: { annotation: Annotation }) {
  const { deleteAnnotation, updateAnnotation, resolveAnnotation } = useSceneStore();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(annotation.text);
  const [flashing, setFlashing] = useState(false);
  const pinRef = useRef<HTMLDivElement>(null);
  const isResolved = annotation.resolved ?? false;

  // Listen for review panel highlight events
  useLayoutEffect(() => {
    function onHighlight(e: Event) {
      const detail = (e as CustomEvent<{ annotationId: string }>).detail;
      if (detail.annotationId !== annotation.id) return;
      setFlashing(true);
      setOpen(true);
      setTimeout(() => setFlashing(false), 1200);
    }
    window.addEventListener('review:highlight-annotation', onHighlight);
    return () => window.removeEventListener('review:highlight-annotation', onHighlight);
  }, [annotation.id]);

  // Set initial position and subscribe to viewport changes imperatively.
  useLayoutEffect(() => {
    function applyPosition(vp: ViewportState) {
      if (!pinRef.current) return;
      const { sx, sy } = worldToScreen(annotation.x, annotation.y, vp);
      pinRef.current.style.left = sx + 'px';
      pinRef.current.style.top = sy + 'px';
    }

    applyPosition(getSpineManager().getViewportState());
    const unsub = getSpineManager().onViewportChange(applyPosition);
    return unsub;
  }, [annotation.id, annotation.x, annotation.y]);

  async function save() {
    await updateAnnotation(annotation.id, draft);
    setEditing(false);
  }

  return (
    <div
      ref={pinRef}
      data-testid={`annotation-pin-${annotation.id}`}
      className="absolute z-30"
      style={{ transform: 'translate(-50%, -100%)' }}
    >
      {/* Pin icon */}
      <button
        onClick={() => setOpen((v) => !v)}
        className={`w-6 h-6 rounded-full border-2 border-white shadow-md flex items-center justify-center text-white text-[10px] font-bold transition-all ${
          flashing ? 'scale-125 ring-2 ring-accent/60 ring-offset-1' : 'hover:scale-110'
        } ${isResolved ? 'bg-emerald-500' : 'bg-accent'}`}
        title={isResolved ? 'Resolved annotation' : 'View annotation'}
      >
        {isResolved ? '✓' : '✎'}
      </button>

      {/* Popover */}
      {open && (
        <div
          data-testid={`annotation-popover-${annotation.id}`}
          className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 bg-bg-elevated border border-border rounded-lg shadow-xl p-3 space-y-2"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Resolved toggle */}
          <button
            onClick={() => resolveAnnotation(annotation.id, !isResolved)}
            className={`flex items-center gap-1.5 text-[10px] font-mono px-2 py-0.5 rounded transition-colors ${
              isResolved
                ? 'text-emerald-400 bg-emerald-500/10 hover:bg-emerald-500/20'
                : 'text-text-tertiary hover:text-emerald-400 hover:bg-emerald-500/10'
            }`}
            data-testid={`annotation-resolve-${annotation.id}`}
          >
            {isResolved ? <CheckCircle size={11} weight="fill" /> : <Circle size={11} />}
            {isResolved ? 'Resolved' : 'Mark resolved'}
          </button>

          {editing ? (
            <>
              <textarea
                autoFocus
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Escape') setEditing(false); e.stopPropagation(); }}
                className="w-full bg-bg-input border border-border rounded text-xs font-sans text-text-primary p-1.5 resize-none outline-none focus:border-accent"
                rows={3}
                data-testid="annotation-text-input"
              />
              <div className="flex gap-1 justify-end">
                <button
                  onClick={() => setEditing(false)}
                  className="text-[10px] font-mono text-text-tertiary hover:text-text-primary px-2 py-0.5 rounded hover:bg-bg-hover"
                >Cancel</button>
                <button
                  onClick={save}
                  className="text-[10px] font-mono text-white bg-accent hover:bg-accent/90 px-2 py-0.5 rounded"
                  data-testid="annotation-save-btn"
                >Save</button>
              </div>
            </>
          ) : (
            <>
              <p className={`text-xs font-sans whitespace-pre-wrap ${isResolved ? 'text-text-tertiary line-through' : 'text-text-primary'}`}>
                {annotation.text || <em className="text-text-tertiary not-italic">No text</em>}
              </p>
              <div className="flex gap-1 justify-end">
                <button
                  onClick={() => setEditing(true)}
                  className="text-[10px] font-mono text-text-tertiary hover:text-accent px-2 py-0.5 rounded hover:bg-accent-muted"
                >Edit</button>
                <button
                  onClick={() => { deleteAnnotation(annotation.id); setOpen(false); }}
                  className="text-[10px] font-mono text-text-tertiary hover:text-error px-2 py-0.5 rounded hover:bg-error-muted"
                  data-testid={`annotation-delete-${annotation.id}`}
                >Delete</button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function AnnotationsOverlay() {
  const { annotations, addAnnotation } = useSceneStore();
  const activeTool = useToolStore((s) => s.activeTool);
  const [pendingPos, setPendingPos] = useState<{ sx: number; sy: number; wx: number; wy: number } | null>(null);
  const [pendingText, setPendingText] = useState('');
  const [filter, setFilter] = useState<AnnotationFilter>('all');

  const isAnnotateMode = activeTool === 'annotate';

  const visibleAnnotations = annotations.filter((a) => {
    if (filter === 'open') return !a.resolved;
    if (filter === 'resolved') return a.resolved;
    return true;
  });

  function handleCanvasClick(e: React.MouseEvent<HTMLDivElement>) {
    if (!isAnnotateMode) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const sx = e.clientX - rect.left;
    const sy = e.clientY - rect.top;
    // Use live viewport state from manager rather than stale vpState prop
    const { wx, wy } = screenToWorld(sx, sy, getSpineManager().getViewportState());
    setPendingPos({ sx, sy, wx, wy });
    setPendingText('');
  }

  async function submitPending() {
    if (!pendingPos) return;
    await addAnnotation(pendingPos.wx, pendingPos.wy, pendingText);
    setPendingPos(null);
    setPendingText('');
  }

  const openCount = annotations.filter((a) => !a.resolved).length;
  const resolvedCount = annotations.filter((a) => a.resolved).length;

  return (
    <div
      className={`absolute inset-0 z-20 pointer-events-none ${isAnnotateMode ? 'pointer-events-auto cursor-crosshair' : ''}`}
      data-testid="annotations-overlay"
      onClick={isAnnotateMode ? handleCanvasClick : undefined}
    >
      {/* Filter bar — only visible when there are annotations */}
      {annotations.length > 0 && (
        <div
          className="absolute top-8 left-1/2 -translate-x-1/2 z-40 flex items-center gap-1 bg-bg-elevated/85 backdrop-blur-sm border border-border rounded-full px-2 py-1 pointer-events-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {([
            { key: 'all',      label: `All (${annotations.length})` },
            { key: 'open',     label: `Open (${openCount})` },
            { key: 'resolved', label: `Resolved (${resolvedCount})` },
          ] as const).map(({ key, label }) => (
            <button
              key={key}
              data-testid={`annotation-filter-${key}`}
              onClick={() => setFilter(key)}
              className={`px-2.5 py-0.5 rounded-full text-[10px] font-mono transition-colors ${
                filter === key
                  ? 'bg-accent text-white'
                  : 'text-text-tertiary hover:text-text-primary hover:bg-bg-hover'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {/* Existing annotation pins — each updates its own position imperatively */}
      {visibleAnnotations.map((a) => (
        <div key={a.id} className="pointer-events-auto" onClick={(e) => e.stopPropagation()}>
          <AnnotationPin annotation={a} />
        </div>
      ))}

      {/* Pending new annotation input */}
      {pendingPos && (
        <div
          data-testid="annotation-new-popover"
          className="absolute z-40 w-52 bg-bg-elevated border border-accent rounded-lg shadow-xl p-3 space-y-2 pointer-events-auto"
          style={{ left: pendingPos.sx, top: pendingPos.sy, transform: 'translate(-50%, -110%)' }}
          onClick={(e) => e.stopPropagation()}
        >
          <p className="text-[9px] font-mono text-text-tertiary uppercase tracking-wide">New annotation</p>
          <textarea
            autoFocus
            value={pendingText}
            onChange={(e) => setPendingText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitPending(); }
              if (e.key === 'Escape') { setPendingPos(null); }
              e.stopPropagation();
            }}
            placeholder="Type a comment… (Enter to save)"
            className="w-full bg-bg-input border border-border rounded text-xs font-sans text-text-primary p-1.5 resize-none outline-none focus:border-accent"
            rows={3}
            data-testid="annotation-new-input"
          />
          <div className="flex gap-1 justify-end">
            <button
              onClick={() => setPendingPos(null)}
              className="text-[10px] font-mono text-text-tertiary hover:text-text-primary px-2 py-0.5 rounded hover:bg-bg-hover"
            >Cancel</button>
            <button
              onClick={submitPending}
              className="text-[10px] font-mono text-white bg-accent hover:bg-accent/90 px-2 py-0.5 rounded"
              data-testid="annotation-submit-btn"
            >Add</button>
          </div>
        </div>
      )}
    </div>
  );
}
